import { useCallback, useEffect, useMemo, useState } from 'react'
import { FilterBar } from '../components/common/FilterBar'
import { AlertCircle, CalendarX2, CarFront, ShieldAlert, ShieldCheck, UserCog } from 'lucide-react'
import { useGetComplianceExpiries } from '../hooks/backend/fleet'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Input } from '../lib/shadcn/input'
import { Switch } from '../lib/shadcn/switch'
import { Label } from '../lib/shadcn/label'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { ExpiryDateDialog } from '../components/fleet/ExpiryDateDialog'
import {
  EXPIRY_KINDS,
  FILTERS,
  KIND_LABEL,
  daysWord,
  expiryChip,
  isBlind,
  rowMatchesFilter,
  summaryZeroTone,
  type ExpiryFeed,
  type ExpiryFilter,
  type ExpiryKind,
  type ExpiryRow,
  type ExpiryState,
} from '../utils/expiries'

/**
 * Expiries: the licence disc, the insurance and the vehicle inspection on every
 * car in the fleet, and the sixty-day task raised against each.
 *
 * WHAT THIS SCREEN IS FOR, stated plainly because the design follows from it.
 * A lapsed disc does not degrade a car's earnings, it stops them: the vehicle
 * comes off the road and the driver in it can be arrested carrying a fare-paying
 * passenger. The same is true of cover and of the inspection certificate. So the
 * question the page answers is not "how are we doing", it is "which cars stop,
 * and when".
 *
 * NOT ON FILE IS RENDERED IN AMBER, NEVER GREY AND NEVER GREEN, and it has its
 * own count in the header beside expired and due. When this page was built the
 * fleet held no insurance date and no inspection date for a single vehicle, and
 * eight of the twenty-seven cars in scope had no disc date either. Those blanks
 * are the true state of the fleet's compliance position and the screen has to say
 * so out loud: a compliance screen that reports green because nobody captured the
 * dates is worse than no screen.
 *
 * The first honest output of this page is therefore a collection job, not a
 * notification.
 */
function cellFor(row: ExpiryRow, kind: ExpiryKind): { state: ExpiryState | null; on: string | null } {
  if (kind === 'licence_disc') return { state: row.licence_disc_state, on: row.licence_disc_expires_on }
  if (kind === 'insurance') return { state: row.insurance_state, on: row.insurance_expires_on }
  return { state: row.inspection_state, on: row.inspection_expires_on }
}

export default function Expiries() {
  const { loading: accessLoading, canView, caption } = useCapabilities()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<ExpiryFilter>('attention')
  const [drivingOnly, setDrivingOnly] = useState(false)

  const feed = useGetComplianceExpiries<ExpiryFeed>()
  const trigger = feed.trigger

  const load = useCallback(() => {
    trigger({ scope: 'in_fleet' }, { skipCache: true })
  }, [trigger])

  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  const allRows = feed.data?.rows ?? []
  const summary = feed.data?.summary ?? []
  const canEdit = feed.data?.can_edit === true

  const counts = useMemo(() => {
    const c: Record<ExpiryFilter, number> = { attention: 0, lapsed: 0, unknown: 0, all: allRows.length }
    for (const r of allRows) {
      if (rowMatchesFilter(r, 'attention')) c.attention += 1
      if (rowMatchesFilter(r, 'lapsed')) c.lapsed += 1
      if (rowMatchesFilter(r, 'unknown')) c.unknown += 1
    }
    return c
  }, [allRows])

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    return allRows.filter((r) => {
      if (!rowMatchesFilter(r, filter)) return false
      if (drivingOnly && r.operational !== true) return false
      if (term) {
        const hay = [r.plate, r.vehicle_name, r.owner_name, r.manager_name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        if (!hay.includes(term)) return false
      }
      return true
    })
  }, [allRows, filter, drivingOnly, search])

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            Vehicle expiries are available to signed-in staff only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <CalendarX2 className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Expiries</h1>
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
            {feed.loading ? '…' : `${rows.length} of ${allRows.length} vehicles`}
          </span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>

        {/* Per kind: expired, due inside sixty days, and NOT ON FILE, always all
            three together. The third is the one that decides how much the first
            two are worth. */}
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          {summary.map((s) => {
            const due = s.due_7 + s.due_30 + s.due_60
            // BLIND IS NOT CLEAN. When no vehicle has a date on file, "Expired 0"
            // and "Due in 60 0" are arithmetically true and completely
            // misleading, and rendering them green would tell the operator the
            // fleet is covered when nobody has checked. On the day this shipped
            // that was the state of insurance and of the inspection certificate
            // across all 27 cars. Zeros go neutral, and the card says why.
            const blind = isBlind(s)
            return (
              <div key={s.kind} className="rounded-md border border-border bg-background p-2.5">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {s.kind_label}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <Chip tone={s.lapsed > 0 ? 'red' : summaryZeroTone(s)}>
                    Expired<span className="ml-1 font-semibold tabular-nums">{s.lapsed}</span>
                  </Chip>
                  <Chip tone={due > 0 ? 'amber' : summaryZeroTone(s)}>
                    Due in 60<span className="ml-1 font-semibold tabular-nums">{due}</span>
                  </Chip>
                  <Chip tone={s.not_on_file > 0 ? 'amber' : 'green'}>
                    Not on file<span className="ml-1 font-semibold tabular-nums">{s.not_on_file}</span>
                  </Chip>
                </div>
                {blind && (
                  <p className="mt-1.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">
                    No date on file for a single vehicle, so the two zeros above are not evidence of
                    anything. Collect the dates first.
                  </p>
                )}
                {(s.lapsed_and_driving > 0 || s.unknown_and_driving > 0) && (
                  <p className="mt-1.5 text-[11px] text-rose-700 dark:text-rose-300">
                    {s.lapsed_and_driving > 0 && `${s.lapsed_and_driving} on the road past the date. `}
                    {s.unknown_and_driving > 0 && `${s.unknown_and_driving} on the road with no date on file.`}
                  </p>
                )}
              </div>
            )
          })}
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {FILTERS.map((f) => {
            const active = filter === f.value
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  active
                    ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                    : 'border-border bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                {f.label}
                <span className="tabular-nums opacity-70">{counts[f.value]}</span>
              </button>
            )
          })}
        </div>
      </div>

      <FilterBar
        activeCount={(search.trim() ? 1 : 0) + (drivingOnly ? 1 : 0)}
        summary={[search.trim() ? `"${search.trim()}"` : null, drivingOnly ? 'on the road today' : null]
          .filter(Boolean)
          .join(', ')}
        trailing={`${rows.length} of ${allRows.length}`}
      >
        <div className="space-y-2 p-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by registration, owner or manager"
          />
          <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5">
            <Switch id="driving-only" checked={drivingOnly} onCheckedChange={setDrivingOnly} />
            <Label htmlFor="driving-only" className="cursor-pointer text-xs font-normal">
              Only cars on the road today
            </Label>
          </div>
        </div>
      </FilterBar>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {feed.error ? (
          <div className="m-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{feed.error}</span>
          </div>
        ) : feed.loading && allRows.length === 0 ? (
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-muted-foreground">
            <CarFront className="h-6 w-6" />
            Nothing matches that filter.
          </div>
        ) : (
          <ul>
            {rows.map((r) => (
              <li key={r.vehicle_id}>
                <div
                  className={cn(
                    'border-b border-border px-3 py-3',
                    r.lapsed_count > 0 && 'bg-rose-50/50 dark:bg-rose-950/20',
                  )}
                >
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-mono text-sm font-bold uppercase tracking-wide text-foreground">
                      {r.plate || '—'}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {r.vehicle_name || 'Unknown vehicle'}
                    </span>
                    {r.operational === true ? (
                      <Chip tone="green">On the road</Chip>
                    ) : (
                      <Chip tone="neutral">Not operational</Chip>
                    )}
                    {r.manager_name && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                        <UserCog className="h-3 w-3" />
                        {r.manager_name}
                      </span>
                    )}
                    {r.owner_name && (
                      <span className="text-[11px] text-muted-foreground">{r.owner_name}</span>
                    )}
                  </div>

                  <div className="mt-2 grid gap-2 sm:grid-cols-3">
                    {EXPIRY_KINDS.map((kind) => {
                      const cell = cellFor(r, kind)
                      const chip = expiryChip(cell.state, cell.on)
                      const days =
                        cell.on === null
                          ? null
                          : Math.round(
                              (new Date(`${cell.on}T00:00:00`).getTime() -
                                new Date(new Date().toDateString()).getTime()) /
                                86_400_000,
                            )
                      return (
                        <div key={kind} className="rounded-md border border-border bg-background px-2.5 py-2">
                          <div className="text-[11px] font-medium text-muted-foreground">
                            {KIND_LABEL[kind]}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            {canEdit ? (
                              <ExpiryDateDialog
                                row={r}
                                kind={kind}
                                state={cell.state}
                                expiresOn={cell.on}
                                onSaved={load}
                              />
                            ) : (
                              <Chip tone={chip.tone}>{chip.label}</Chip>
                            )}
                            {cell.on && (
                              <span className="text-[11px] text-muted-foreground">{daysWord(days)}</span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
